/**
 * YEREL GUN SINIRI — Europe/Istanbul
 *
 * ================================================================
 * NEDEN VAR: HER GECE 00:00-03:00 ARASI YANLIS GUN GOSTERILIYORDU
 * ================================================================
 * Olculdu (04.09.2026, denetim oturumu buldu, iki kez bagimsiz
 * dogrulandi). `gunBasi` ve `haftaBasi` "su an"i UTC BILESENIYLE
 * gune ceviriyordu:
 *
 *     new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()))
 *
 * Turkiye UTC+3. Yerel 00:00-02:59 arasinda `new Date()`in UTC gunu
 * HALA DUNKU GUN. Olculen sonuc:
 *
 *     Istanbul 07.09 00:30  ->  kod "06.09" diyor   (DUN)
 *     Istanbul 07.09 03:30  ->  kod "07.09" diyor   (dogru)
 *
 * Haftalik programda daha agir: Pazartesi 00:00-03:00 arasi BUTUN
 * HAFTA kayiyor ve ogrenci onceki haftanin programini goruyor.
 *
 * ================================================================
 * NEDEN SESSIZ — VE NEDEN TESTLER GORMEDI
 * ================================================================
 * Istemci ile sunucu AYNI kurali kopyaladigi icin AYNI YANLIS
 * CEVAPTA ANLASIYORLAR. Ekran bozuk gorunmuyor: ogrenci gece
 * 00:30'da dunku gorevleri "bugun" diye goruyor, hicbir hata
 * cikmiyor.
 *
 * BIRIM TESTLERININ GORMEME SEBEBI ISE SAAT DILIMI DEGIL — ilk yazdigim
 * gerekce ("kosu ortami UTC") YANLISTI ve denetim oturumu duzeltti.
 * Olculdu: bu makine zaten `Europe/Istanbul`, offset -180, `TZ`
 * degiskeni yok. Yani testler bastan beri Istanbul'da kosuyordu.
 *
 * Sebep daha basit ve daha ogretici: HICBIR TEST GECE PENCERESINI
 * DENEMEDI. Ortami suclamak, "bizde farkli calisiyor" diyerek kapanan
 * bir aciklama olurdu; gercek sebep kapsam eksikligiydi ve o
 * duzeltilebilir. Bu dosyanin testleri artik 00:00-03:00'i aciktan
 * deniyor.
 *
 * Kitle sinava hazirlanan lise ogrencileri; gece yarisindan sonra
 * calismak bu grupta istisna degil KURAL. Yani hatanin penceresi
 * "kimsenin uygulamayi acmadigi saat" degil.
 *
 * ================================================================
 * AYRIM: "SU AN" ILE "SAKLANMIS TARIH" AYNI SEY DEGIL
 * ================================================================
 * Bu modul yalnizca DUVAR SAATI ("su an") icin. Veritabaninda
 * `itemDate` gibi alanlar ZATEN Turk takvim gununun UTC gece
 * yarisi olarak saklaniyor (olculdu: 2026-09-06 00:00:00) ve
 * onlarda UTC bileseni DOGRU. Saklanmis bir tarihi buradan
 * gecirmek onu bir gun GERI atardi — yani korgu duzeltme yeni bir
 * hata olurdu.
 *
 * CLAUDE.md: "Saat dilimi Europe/Istanbul; veritabaninda UTC."
 * Niyet zaten buydu, uygulama UTC'de kalmisti.
 */

/** Sistemin tek saat dilimi. Cok bolgeli degil — CLAUDE.md. */
export const SAAT_DILIMI = "Europe/Istanbul";

/**
 * Verilen ana karsilik gelen YEREL takvim gununun UTC gece yarisi.
 *
 * `Intl` kullaniliyor, sabit +3 DEGIL: Turkiye 2016'dan beri yaz
 * saati uygulamiyor ama bunu koda GOMMEK, politika degistiginde
 * sessizce yanlisa donen bir varsayim birakirdi. `Intl` isletim
 * sisteminin saat dilimi veritabanindan okuyor.
 */
export function yerelGunBasi(t: Date = new Date()): Date {
  // en-CA "YYYY-MM-DD" veriyor — ayristirmasi en az tuzakli bicim.
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: SAAT_DILIMI,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(t);
  return new Date(`${ymd}T00:00:00.000Z`);
}

/**
 * Yerel takvime gore icinde bulunulan HAFTANIN Pazartesisi (UTC gece
 * yarisi).
 *
 * Gun adi yerel takvimden okunuyor: UTC gunune bakmak, gece
 * 00:00-03:00 arasi Pazartesi'yi Pazar sanmaya ve butun haftayi bir
 * hafta geri atmaya yol aciyordu.
 */
export function yerelHaftaBasi(t: Date = new Date()): Date {
  const g = yerelGunBasi(t);
  // getUTCDay guvenli: `g` zaten UTC gece yarisi ve yerel tarihi tasiyor.
  const gun = g.getUTCDay(); // 0 = Pazar
  const geri = gun === 0 ? 6 : gun - 1;
  return new Date(g.getTime() - geri * 86_400_000);
}

/**
 * Bir anin Europe/Istanbul'daki UTC farki (dakika; Istanbul icin +180).
 *
 * `Intl` ile, sabit +3 DEGIL — yukaridaki `yerelGunBasi` gerekcesi.
 */
function yerelFarkDakika(t: Date): number {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: SAAT_DILIMI,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(t)
      .map((x) => [x.type, x.value]),
  );
  const yerelGibi = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
  );
  return Math.round((yerelGibi - Math.floor(t.getTime() / 60_000) * 60_000) / 60_000);
}

/**
 * YEREL DUVAR SAATI -> UTC AN — F-EXM-01a (A3, 05.10.2026).
 *
 * ================================================================
 * NEDEN VAR: PANEL "00:00" GONDERIYORDU AMA HANGI 00:00?
 * ================================================================
 * Deneme penceresi `${gun}T00:00:00` diye — SAAT DILIMI OLMADAN —
 * gonderiliyordu. Sunucu bunu KENDI saat diliminde yorumluyor: UTC
 * calisan uretim sunucusunda "00:00" Istanbul'da 03:00 oluyordu ve
 * bitis "23:59" ertesi gun 02:59'a kayiyordu. Bu fonksiyon yerel
 * gun + saati acik bir ana ceviriyor; istemci `toISOString()` ile
 * gonderiyor ve sunucu ofsetsiz dizgiyi kabul ETMIYOR.
 *
 * @param gun  "YYYY-MM-DD" (Istanbul takvimi)
 * @param saat "HH:MM" (Istanbul duvar saati)
 */
export function yerelAn(gun: string, saat: string): Date {
  const [y, a, g] = gun.split("-").map(Number);
  const [s, d] = saat.split(":").map(Number);
  const yerelGibi = Date.UTC(y!, a! - 1, g!, s!, d!);
  // Ilk tahmin: farki tahmini anda olc, cikar. Yaz saati gecisinde
  // fark tahmin ile gercek an arasinda degisebilir; ikinci tur onu
  // duzeltiyor (Turkiye bugun gecis yapmiyor, ama kurala gomulmuyor).
  let an = yerelGibi - yerelFarkDakika(new Date(yerelGibi)) * 60_000;
  an = yerelGibi - yerelFarkDakika(new Date(an)) * 60_000;
  return new Date(an);
}

/** Bir ani Istanbul takvim gunu + duvar saatine ayirir — `yerelAn`in tersi. */
export function yerelParcalar(t: Date | string): { gun: string; saat: string } {
  const an = new Date(t);
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: SAAT_DILIMI,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(an)
      .map((x) => [x.type, x.value]),
  );
  return { gun: `${p.year}-${p.month}-${p.day}`, saat: `${p.hour}:${p.minute}` };
}

/**
 * Iki an arasindaki sure, okunur metin: "2 sa 15 dk", "3 gün 4 sa".
 * Negatif ya da sifir sure icin `null` — ekran "gecersiz" der, "0 dk" degil.
 */
export function pencereSuresiMetni(bas: Date | string, bit: Date | string): string | null {
  const dk = Math.round((new Date(bit).getTime() - new Date(bas).getTime()) / 60_000);
  if (!Number.isFinite(dk) || dk <= 0) return null;
  const gun = Math.floor(dk / 1440);
  const sa = Math.floor((dk % 1440) / 60);
  const kalan = dk % 60;
  const parca: string[] = [];
  if (gun > 0) parca.push(`${gun} gün`);
  if (sa > 0) parca.push(`${sa} sa`);
  if (kalan > 0 && gun === 0) parca.push(`${kalan} dk`);
  return parca.join(" ") || null;
}
